import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ShieldAlert } from "lucide-react";
import { ROLES, type Role } from "@hisense/shared";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/app/auth";
import { useUserSecurityEvents, useResetUserPassword, useUpdateUser, type UserRow } from "@/api/hooks";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { translateStatus } from "@/domain/i18n";

const STATUSES = ["active", "disabled"] as const;

/** Server lockout guards → the messages legacy only ever showed client-side. */
const GUARD_KEY: Record<string, string> = {
  self_downgrade_blocked: "users.selfDowngradeBlocked",
  last_admin: "users.lastAdminProtected",
};

function guardMessage(err: unknown, t: TFunction, fallbackKey: string): string {
  const code = err instanceof ApiClientError ? err.code : "";
  const key = GUARD_KEY[code];
  return key ? t(key) : getLocalizedError(err, t, fallbackKey);
}

/**
 * Edit one account: display name, role, status, plus an admin password reset.
 * Mount it with `key={user.id}` — the local form state must never bleed from one
 * cached user row to the next.
 */
export function UserEditPanel({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { user: me, logout } = useAuth();
  const updateUser = useUpdateUser();
  const resetPassword = useResetUserPassword();
  const history=useUserSecurityEvents(user.id);
  const [acknowledged,setAcknowledged]=useState(false);

  const [form, setForm] = useState({
    displayName: user.displayName,
    role: user.role as Role,
    status: user.status as (typeof STATUSES)[number],
  });
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);

  const isSelf = me?.id === user.id;
  /** Same predicate the server uses — kept only to warn early; the server decides. */
  const removesAdmin = user.role === "admin" && (form.role !== "admin" || form.status !== "active");
  const selfLocked = isSelf && user.role === "admin";
  const pwMismatch = pw.confirm.length > 0 && pw.password !== pw.confirm;

  const save = async () => {
    setError(null);
    try {
      await updateUser.mutateAsync({ id: user.id, ...form });
      onClose();
    } catch (err) {
      setConfirming(false);
      setError(guardMessage(err, t, "users.errorUpdate"));
    }
  };

  const submitProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (removesAdmin && !confirming) {
      setConfirming(true);
      return;
    }
    void save();
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setPwDone(false);
    if (!acknowledged) return;
    if (pw.password !== pw.confirm) {
      setPwError(t("users.passwordMismatch"));
      return;
    }
    try {
      await resetPassword.mutateAsync({ id: user.id, password: pw.password });
      setPw({ password: "", confirm: "" });
      setPwDone(true);setAcknowledged(false);
      if(isSelf) await logout();
    } catch (err) {
      setPwError(guardMessage(err, t, "users.errorUpdate"));
    }
  };

  return (
    <Modal
      eyebrow={user.email}
      title={t("users.editUser")}
      closeLabel={t("common.cancel")}
      onClose={() => { if(!updateUser.isPending && !resetPassword.isPending) onClose(); }}
      className="max-w-3xl"
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
      <form onSubmit={submitProfile} className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="sm:col-span-2 lg:col-span-3"><h3 className="font-medium">{t("userWorkspace.profile")}</h3><p className="mt-1 text-xs text-ink-soft">{t("userWorkspace.profileHint")}</p></div>
        <div>
          <FieldLabel htmlFor="ue-name">{t("common.name")}</FieldLabel>
          <Input
            id="ue-name"
            value={form.displayName}
            required
            maxLength={120}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </div>
        <div>
          <FieldLabel htmlFor="ue-role">{t("users.changeRole")}</FieldLabel>
          <Select
            id="ue-role"
            containerClassName="w-full"
            value={form.role}
            disabled={selfLocked}
            onChange={(e) => { setConfirming(false); setForm({ ...form, role: e.target.value as Role }); }}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{t(`roles.${r}`)}</option>
            ))}
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="ue-status">{t("common.status")}</FieldLabel>
          <Select
            id="ue-status"
            containerClassName="w-full"
            value={form.status}
            disabled={selfLocked}
            onChange={(e) => {
              setConfirming(false);
              setForm({ ...form, status: e.target.value as (typeof STATUSES)[number] });
            }}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{translateStatus(t, s)}</option>
            ))}
          </Select>
        </div>

        {selfLocked && (
          <p className="text-xs text-ink-faint sm:col-span-2 lg:col-span-3">
            {t("users.selfDowngradeBlocked")}
          </p>
        )}
        {confirming && (
          <p className="flex items-center gap-2 rounded-sm bg-warn-tint px-3 py-2 text-xs text-warn sm:col-span-2 lg:col-span-3">
            <ShieldAlert className="size-4 shrink-0" />
            {t("users.confirmRoleChange", { email: user.email })}
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger sm:col-span-2 lg:col-span-3">
            {error}
          </p>
        )}

        <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
          <Button type="submit" size="sm" variant={confirming ? "danger" : "primary"} disabled={updateUser.isPending}>
            {updateUser.isPending ? t("common.saving") : t("common.save")}
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={updateUser.isPending || resetPassword.isPending} onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>

      <form
        onSubmit={submitPassword}
        className="grid grid-cols-1 gap-3 border-t border-line p-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        <div className="sm:col-span-2 lg:col-span-3"><h3 className="font-medium">{t("userWorkspace.resetTitle")}</h3><p className="mt-1 text-xs text-ink-soft">{t("userWorkspace.resetHint",{email:user.email})}</p></div>
        <div>
          <FieldLabel htmlFor="ue-pass">{t("common.password")}</FieldLabel>
          <Input
            id="ue-pass"
            type="password"
            autoComplete="new-password"
            value={pw.password}
            required
            minLength={8}
            maxLength={200}
            onChange={(e) => { setPwDone(false); setPw({ ...pw, password: e.target.value }); }}
          />
        </div>
        <div>
          <FieldLabel htmlFor="ue-pass2">{t("users.confirmPassword")}</FieldLabel>
          <Input
            id="ue-pass2"
            type="password"
            autoComplete="new-password"
            value={pw.confirm}
            required
            minLength={8}
            maxLength={200}
            invalid={pwMismatch}
            aria-invalid={pwMismatch}
            aria-describedby={pwMismatch ? "ue-pass-mismatch" : undefined}
            onChange={(e) => { setPwDone(false); setPw({ ...pw, confirm: e.target.value }); }}
          />
          {pwMismatch && (
            <p id="ue-pass-mismatch" className="mt-1 text-xs text-danger">{t("users.passwordMismatch")}</p>
          )}
        </div>
        <label className="flex items-start gap-2 text-xs text-ink-soft sm:col-span-2 lg:col-span-3"><input type="checkbox" checked={acknowledged} onChange={e=>setAcknowledged(e.target.checked)}/>{t("userWorkspace.acknowledge")}</label>
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-3">
          <Button type="submit" size="sm" variant="secondary" disabled={resetPassword.isPending || pwMismatch || !acknowledged}>
            {resetPassword.isPending ? t("common.saving") : t("users.resetPassword")}
          </Button>
          {pwDone && <span className="pb-2 text-xs text-ok">{t("userWorkspace.resetDone")}</span>}
        </div>
        {pwError && (
          <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger sm:col-span-2 lg:col-span-3">
            {pwError}
          </p>
        )}
      </form>
      <section className="border-t border-line p-5"><h3 className="font-medium">{t("userWorkspace.history")}</h3><p className="mt-1 text-xs text-ink-faint">{t("userWorkspace.historyHint")}</p>
      {history.isLoading ? <p>{t("common.loading")}</p> : history.error ? <div role="alert">{t("userWorkspace.historyError")}<Button size="sm" variant="ghost" onClick={()=>history.refetch()}>{t("common.retry")}</Button></div> : !history.data?.length ? <p className="mt-3 text-sm text-ink-soft">{t("userWorkspace.noHistory")}</p> : <ul className="mt-3 divide-y divide-line">{history.data.map(event=><li key={event.id} className="py-2 text-xs"><div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{t(`userWorkspace.event_${event.action}`)}</span><time className="font-mono tabular text-ink-faint">{new Date(event.createdAt).toLocaleString(i18n.language)}</time></div><p className="mt-1 text-ink-soft">{t("userWorkspace.actor")}: {event.actorName||t("userWorkspace.system")}{event.detail ? ` · ${event.detail.split(" → ").map(role=>ROLES.includes(role as Role)?t(`roles.${role}`):role).join(" → ")}` : ""}</p></li>)}</ul>}
      </section>
      </div>
    </Modal>
  );
}
